/**
 * The student / professor identity palette (PRD-59).
 *
 * Four hues, assigned per person by the backend (`Student.color`,
 * `Professor.color`) and used to tell one row apart from the next at a glance.
 *
 * **These deliberately stay raw hues rather than moving to the design tokens.**
 * The token layer names five ROLES — accent, success, attention, danger,
 * neutral — and an identity is not a role: a student whose colour happens to be
 * orange is not a warning, and two students mapped onto the same role stop being
 * distinguishable, which is the one job this palette has. A categorical scale
 * forced into a semantic one is a worse system, not a more consistent one.
 *
 * What WAS wrong is that the scale existed twice, in two spellings: the `Avatar`
 * primitive drew solid 500s with white text, and `ProfileHeader` re-spelled the
 * same four names as soft 100s with 700 text — so the same student was a solid
 * blue circle in the roster and a pale blue circle on their own profile. One
 * scale, two variants, declared here.
 *
 * If these ever do become tokens they need four identity tokens of their own
 * (`--ds-identity-1…4`), not a remap onto the roles. That is recorded as a
 * follow-up in PRD-59.
 */
export const AVATAR_TONE_NAMES = ["purple", "blue", "green", "orange"] as const
export type AvatarToneName = (typeof AVATAR_TONE_NAMES)[number]

/**
 * Solid fill, white text — the small circles in lists and headers.
 *
 * Shades chosen for the white initials: the 500s read 2.2:1 (green) to 4.1:1
 * (purple) against white, all below AA for 12px bold; these are 4.9:1 or better.
 */
export const AVATAR_SOLID: Record<string, string> = {
  purple: "bg-purple-600",
  blue: "bg-blue-600",
  green: "bg-green-700",
  orange: "bg-orange-700",
}

/** Soft fill, dark text — the large circle on a person's own page. */
export const AVATAR_SOFT: Record<string, string> = {
  purple: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-200",
  blue: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-200",
  green: "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-200",
  orange: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200",
}

export const avatarSolid = (color: string | null | undefined): string =>
  AVATAR_SOLID[color ?? ""] ?? "bg-border-strong"

export const avatarSoft = (color: string | null | undefined): string =>
  AVATAR_SOFT[color ?? ""] ?? AVATAR_SOFT.purple!
